import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { setGlobalNavigate } from '@/lib/navigation';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache, type Query } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from 'react-hot-toast';
import { platform } from '@/utils/platform';
import { TOASTER_PROPS } from '@/utils/toast';
import { errorCoordinator } from '@/services/errorCoordinator';
import { setLoading } from '@/lib/globalLoading';
import { useIsAuthenticated, LoginPage, RegisterPage } from '@/features/auth';
import GoogleCallbackPage from '@/features/auth/pages/GoogleCallbackPage';
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/features/auth/pages/ResetPasswordPage';
import HomePage from '@/features/home/pages/HomePage';
import { DashboardPage } from '@/features/dashboard';
import { FeedPage } from '@/features/feed';
import { VideosPage, VideoUploadPage, VideoDetailPage } from '@/features/videos';
import { CommentsPage } from '@/features/comments';
import { SearchPage } from '@/features/search';
import { ExplorePage } from '@/features/explore';
import { PublicProfilePage } from '@/features/users/pages/PublicProfilePage';
import {
  AdminDashboardPage,
  ActivityLogPage,
  SystemSettingsPage,
  FeatureFlagsPage,
  RolesManagementPage,
  AdminSidebarLayout,
  UserApprovalsPage,
  UserImpersonationPage,
  DesignTokensPage,
  DesignComponentsPage,
  DesignPatternsPage,
  RssFeedsManagementPage,
  ColorManagerPage,
} from '@/features/admin';
import { UsersManagementPage, UserDetailPage, ProfileSettingsPage } from '@/features/users';
import { NotificationsPage } from '@/features/notifications';
import { ContactSupportPage } from '@/features/support/pages/ContactSupportPage';
import { EditorPage } from '@/features/editor';
import OnboardingPage from '@/features/auth/pages/OnboardingPage';
import { ProtectedRoute, AdminRoute } from '@/components/ProtectedRoute';
import { AppLayout, HeroDockLayout } from '@/components/layout';
import { DesktopMessagingLayout } from '@/components/drawer';
import { MessagesPage } from '@/features/messaging/pages/MessagesPage';
import { ModalProvider } from '@/components/modal';
import { ThemeProvider } from '@/providers/ThemeProvider';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';

/** Scroll to top on route change */
/** Registers React Router's navigate fn for use outside React context (e.g., Axios interceptors) */
function NavigateRegistrar() {
  const navigate = useNavigate();
  useEffect(() => { setGlobalNavigate(navigate); }, [navigate]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Auth-aware home route: authenticated → /home, otherwise → /login */
function HomeRoute() {
  const isAuthenticated = useIsAuthenticated();
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  return <Navigate to="/login" replace />;
}

/** Catch-all: authenticated → /home, otherwise → /login */
function CatchAllRoute() {
  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to="/home" replace />;
}

/** Auth gate: if already authenticated, redirect to home */
function AuthGate({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/** On desktop, render full MessagingLayout. On mobile, render the messages list
 *  page (bottom nav stays visible). Individual chats use SpatialOverlay in AppLayout. */
function MessagingRoute() {
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  if (!isMobile) return <DesktopMessagingLayout />;
  return <MessagesPage />;
}

function isServerError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'response' in error) {
    const resp = (error as { response?: { status?: number } }).response;
    return (resp?.status ?? 0) >= 500;
  }
  return false;
}

function getSourceKey(error: unknown): string {
  if (error && typeof error === 'object' && 'config' in error) {
    const config = (error as { config?: { method?: string; url?: string } }).config;
    const method = config?.method?.toUpperCase() ?? 'UNKNOWN';
    const url = config?.url ?? '/unknown';
    return `${method}:${url}`;
  }
  return 'UNKNOWN:/unknown';
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (isServerError(error) || !navigator.onLine) {
        errorCoordinator.handleServerError(error, getSourceKey(error));
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isServerError(error) || !navigator.onLine) {
        errorCoordinator.handleServerError(error, getSourceKey(error));
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Drive globalLoading from React Query fetch status
queryClient.getQueryCache().subscribe(() => {
  const fetching = queryClient
    .getQueryCache()
    .getAll()
    .some((q: Query) => q.state.fetchStatus === 'fetching');
  setLoading('react-query', fetching);
});

/** On native, skip GoogleOAuthProvider — native SDK handles auth */
function GoogleAuthWrapper({ children }: { children: ReactNode }) {
  if (platform.isNative) return <>{children}</>;
  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
      {children}
    </GoogleOAuthProvider>
  );
}

function App() {
  useEffect(() => {
    if (platform.isNative) {
      import('@capacitor/splash-screen').then(({ SplashScreen }) => {
        SplashScreen.hide({ fadeOutDuration: 300 });
      });
    }
  }, []);

  return (
    <GoogleAuthWrapper>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <ModalProvider>
      <BrowserRouter>
        <NavigateRegistrar />
        <ScrollToTop />
        <Routes>
          {/* Auth routes — no protection, lightweight */}
          <Route path="/login" element={<AuthGate><LoginPage /></AuthGate>} />
          <Route path="/register" element={<AuthGate><RegisterPage /></AuthGate>} />
          <Route path="/auth/callback" element={<GoogleCallbackPage />} />
          <Route path="/forgot-password" element={<AuthGate><ForgotPasswordPage /></AuthGate>} />
          <Route path="/auth/reset-password" element={<AuthGate><ResetPasswordPage /></AuthGate>} />

          {/* Onboarding — authenticated but no layout */}
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

          {/* Home route — redirects authenticated to reports, unauthenticated to /login */}
          <Route path="/" element={<HomeRoute />} />

          {/* Protected routes with layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Routes with shared persistent hero + dock */}
            <Route element={<HeroDockLayout />}>
              <Route path="home" element={<HomePage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="feed" element={<FeedPage />} />
              <Route path="media" element={<VideosPage />} />
              <Route path="media/upload" element={<VideoUploadPage />} />
              <Route path="media/:id" element={<VideoDetailPage />} />
              <Route path="comments" element={<CommentsPage />} />
              <Route path="explore" element={<ExplorePage />} />
              <Route path="search" element={<SearchPage />} />
              <Route path="profile/:userId" element={<PublicProfilePage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="settings" element={<ProfileSettingsPage />} />
              <Route path="profile" element={<ProfileSettingsPage />} />
              <Route path="support" element={<ContactSupportPage />} />
            </Route>

            {/* Messaging — inside AppLayout so shell stays mounted for spatial transition */}
            <Route path="messages/*" element={<MessagingRoute />} />

            {/* Editor — inside AppLayout (nav rail stays), outside HeroDockLayout (no hero/dock) */}
            <Route path="studio" element={<EditorPage />} />
            <Route path="studio/:projectId" element={<EditorPage />} />
          </Route>

          {/* Admin routes with dedicated sidebar layout */}
          <Route
            path="admin"
            element={
              <AdminRoute>
                <AdminSidebarLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="user-approvals" element={<UserApprovalsPage />} />
            <Route path="users" element={<UsersManagementPage />} />
            <Route path="users/:id" element={<UserDetailPage />} />
            <Route path="roles" element={<RolesManagementPage />} />
            <Route path="user-impersonation" element={<UserImpersonationPage />} />
            <Route path="rss-feeds" element={<RssFeedsManagementPage />} />
            <Route path="activity" element={<ActivityLogPage />} />
            <Route path="colors" element={<ColorManagerPage />} />
            <Route path="settings" element={<SystemSettingsPage />} />
            <Route path="features" element={<FeatureFlagsPage />} />
            {/* Design System */}
            <Route path="design-tokens" element={<DesignTokensPage />} />
            <Route path="design-components" element={<DesignComponentsPage />} />
            <Route path="design-patterns" element={<DesignPatternsPage />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<CatchAllRoute />} />
        </Routes>
      </BrowserRouter>
      <Toaster {...TOASTER_PROPS} />
      </ModalProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </GoogleAuthWrapper>
  );
}

export default App;
